/**
 * @file PositionNode atom — tactical-map position node.
 * @description Renders a colored circle with a count/score value, an optional
 * selection ring, and a role label. All geometry (size, position, colors) is
 * passed as props so call sites carry zero UNSAFE_style.
 * @module components/atoms/PositionNode
 */
import React, { memo, useMemo } from "react";
import { Pressable, StyleSheet, Text, View } from "react-native";

/** Accent color for the selection ring. */
const SELECTION_RING_COLOR = "#007AFF";
/** Line color for ghost (unfilled) nodes. */
const LINE_COLOR = "#FFFFFF80";

/**
 *
 */
export interface PositionNodeProps {
  /** Node circle radius in pixels. */
  readonly nodeRadius: number;
  /** Absolute left position of the node centre on the tactical map. */
  readonly x: number;
  /** Absolute top position of the node centre on the tactical map. */
  readonly y: number;
  /** Text shown inside the circle (count or GPR score). */
  readonly displayValue: string;
  /**
   * Fill color for the circle. Pass `null` to render a ghost (transparent)
   * node with a border instead.
   */
  readonly fillColor: string | null;
  /** Position role label rendered below the circle. */
  readonly roleLabel: string;
  /** Whether this node is currently selected (shows selection ring). */
  readonly isSelected: boolean;
  /** Affects the role-label text-shadow halo for field-line legibility. */
  readonly isDark: boolean;
  /** Callback when the node is tapped. */
  readonly onPress: () => void;
  /** E2E test identifier. */
  readonly testID?: string;
  /** Accessibility label for the pressable. */
  readonly accessibilityLabel?: string;
}

/**
 * A single position node on the tactical map.
 * @param props - PositionNodeProps
 * @param props.nodeRadius - Radius of the node circle in pixels
 * @param props.x - Absolute left position of the node centre
 * @param props.y - Absolute top position of the node centre
 * @param props.displayValue - Text shown inside the circle
 * @param props.fillColor - Fill color, or null for ghost node
 * @param props.roleLabel - Position role label rendered below the circle
 * @param props.isSelected - Whether this node is currently selected
 * @param props.isDark - Whether dark mode is active
 * @param props.onPress - Callback when the node is tapped
 * @param props.testID - E2E test identifier
 * @param props.accessibilityLabel - Accessibility label for the pressable
 */
function PositionNode({
  nodeRadius,
  x,
  y,
  displayValue,
  fillColor,
  roleLabel,
  isSelected,
  isDark,
  onPress,
  testID,
  accessibilityLabel,
}: PositionNodeProps) {
  const diameter = nodeRadius * 2;
  const countFontSize = Math.max(10, Math.round(nodeRadius * 0.7));
  const labelFontSize = Math.max(10, Math.round(nodeRadius * 0.55));
  const isFilled = fillColor !== null;

  const containerStyle = useMemo(
    () => ({
      position: "absolute" as const,
      alignItems: "center" as const,
      left: x - nodeRadius,
      top: y - nodeRadius,
      width: diameter,
      height: diameter,
    }),
    [x, y, nodeRadius, diameter]
  );

  const selectionRingStyle = useMemo(
    () => ({
      position: "absolute" as const,
      borderColor: SELECTION_RING_COLOR,
      borderWidth: 2,
      width: diameter + 8,
      height: diameter + 8,
      top: -4,
      left: -4,
      borderRadius: (diameter + 8) / 2,
    }),
    [diameter]
  );

  const circleStyle = useMemo(
    () => ({
      width: diameter,
      height: diameter,
      borderRadius: nodeRadius,
      backgroundColor: isFilled
        ? (fillColor as string)
        : isDark
          ? "rgba(255,255,255,0.08)"
          : "rgba(0,0,0,0.05)",
      borderWidth: 1.5,
      borderColor: isFilled ? "transparent" : LINE_COLOR,
      alignItems: "center" as const,
      justifyContent: "center" as const,
    }),
    [diameter, nodeRadius, isFilled, fillColor, isDark]
  );

  const countTextStyle = useMemo(
    () => ({
      textAlign: "center" as const,
      fontFamily: "SatoshiBold",
      fontSize: countFontSize,
      color: isFilled ? "#FFFFFF" : LINE_COLOR,
    }),
    [countFontSize, isFilled]
  );

  const roleLabelStyle = useMemo(
    () => ({
      textAlign: "center" as const,
      fontFamily: "SatoshiBold",
      fontSize: labelFontSize,
      letterSpacing: 0.3,
      marginTop: 2,
      ...(isDark
        ? {
            color: "#FFFFFF",
            textShadowColor: "#000000",
            textShadowOffset: { width: 0, height: 0 } as const,
            textShadowRadius: 6,
          }
        : {
            color: "#272625",
            textShadowColor: "#FFFFFF",
            textShadowOffset: { width: 0, height: 0 } as const,
            textShadowRadius: 6,
          }),
    }),
    [labelFontSize, isDark]
  );

  return (
    <View style={containerStyle} testID={testID}>
      <Pressable
        onPress={onPress}
        accessibilityRole="button"
        accessibilityLabel={accessibilityLabel}
      >
        <View style={styles.scaleWrapper}>
          <View style={isSelected ? styles.selectedScale : styles.normalScale}>
            {isSelected && <View style={selectionRingStyle} />}
            <View style={circleStyle}>
              <Text style={countTextStyle}>{displayValue}</Text>
            </View>
          </View>
        </View>
        <Text style={roleLabelStyle}>{roleLabel}</Text>
      </Pressable>
    </View>
  );
}

const styles = StyleSheet.create({
  scaleWrapper: { alignItems: "center" as const },
  normalScale: { transform: [{ scale: 1 }] },
  selectedScale: { transform: [{ scale: 1.12 }] },
});

PositionNode.displayName = "PositionNode";

export default memo(PositionNode);
export { PositionNode };
